/**
 * GenericSwitchModalClassic — Monolithic generic smart switch device modal template.
 *
 * General-purpose single-channel switch control:
 * - Power: large centered ON/OFF toggle
 *
 * Suitable for smart plugs, generic relays, and unknown switch-type devices.
 */
import { useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './GenericSwitchModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const GENERIC_SWITCH_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Power Section ──
  { id: 'power', label: 'Power', icon: 'power_settings_new', slots: [
    { id: 'power-value', label: 'Status Text "ON/OFF"', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'power-btn', label: 'Power Button Circle', editableProps: ['background', 'color', 'borderRadius', 'width', 'height', 'boxShadow', 'glow'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'power-btn-icon', label: 'Power Button Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'power-hint', label: 'Hint Text "Tap to toggle"', editableProps: ['fontSize', 'color', 'fontWeight'] },
  ]},
]

const DEFAULT_SECTION_ORDER = GENERIC_SWITCH_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys)
  return v != null ? String(v) : ''
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GenericSwitchModalClassic({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const isOn = getAttr(deviceState, 'on', 'isOn', 'powerState') === true
    || strAttr(deviceState, 'on', 'isOn', 'powerState') === 'true'

  // ---- Commands ----
  const togglePower = useCallback(() => {
    if (isEditorMode) return
    onCommand(isOn ? 'off' : 'on')
  }, [isEditorMode, isOn, onCommand])

  return (
    <div
      className={`generic-switch-modal-classic${isOn ? '' : ' is-off'}${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="generic-switch-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="generic-switch-modal-classic__header">
        <button className="generic-switch-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="generic-switch-modal-classic__title" data-modal-slot="header-title">{device.name || 'Smart Switch'}</h2>
        <button
          className="generic-switch-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'power':
            return (
              <div key="power" className={`generic-switch-modal-classic__power-section${hl}`} data-modal-section="power">
                <span
                  className={`generic-switch-modal-classic__power-status${isOn ? ' on' : ' off'}`}
                  data-modal-slot="power-value"
                  data-modal-slot-state={isOn ? 'on' : 'off'}
                >
                  {isOn ? 'ON' : 'OFF'}
                </span>
                <button
                  className={`generic-switch-modal-classic__power-btn${isOn ? ' on' : ''}`}
                  data-modal-slot="power-btn"
                  data-modal-slot-state={isOn ? 'on' : 'off'}
                  onClick={togglePower}
                  aria-label={isOn ? 'Turn off' : 'Turn on'}
                >
                  <span
                    className="material-symbols-rounded"
                    data-modal-slot="power-btn-icon"
                    data-modal-slot-state={isOn ? 'on' : 'off'}
                  >
                    power_settings_new
                  </span>
                </button>
                <span className="generic-switch-modal-classic__hint" data-modal-slot="power-hint">
                  Tap to toggle
                </span>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
